/**
 * Official Vertex product photography (studio pack shots + on-site lifestyle
 * shots supplied by Vertex, Sep 2026), keyed by catalogue route.
 *
 * Every file has a full-size version (`.jpg`, max 1600px) and a thumbnail
 * (`-sm.jpg`, max 700px). Pack shots also have background-removed versions
 * (`-cutout.webp` / `-cutout-sm.webp`, see `cutout()`), used wherever a pack
 * floats over a banner or card.
 */

export type MediaShot = {
  src: string;
  alt: string;
  /** Transparent version of a pack shot (uploaded in the admin). */
  cutout?: string;
};
export type ProductMedia = {
  /** Studio pack shot on white. */
  pack?: MediaShot;
  /** On-site / lifestyle photographs of the product in use. */
  gallery: MediaShot[];
};

const P = '/images/products/';
const shot = (file: string, alt: string): MediaShot => ({ src: `${P}${file}.jpg`, alt });

/** Swap a full-size path for its 700px thumbnail. */
/** Only bundled photos have pre-made thumbnails; admin uploads are used as-is. */
export const thumb = (src: string) => (/^\/images\/(products|events|marmovella)\//.test(src) ? src.replace(/\.jpg$/, '-sm.jpg') : src);

/**
 * Background-removed version of a pack shot (transparent WebP, made with
 * Apple Vision subject lifting). `small` gives the 480px card size.
 */
export const cutout = (packSrc: string, small = false) => packSrc.replace(/\.jpg$/, small ? '-cutout-sm.webp' : '-cutout.webp');

/**
 * The best image to float a pack over a banner or card: the admin-supplied
 * cut-out, else the pre-made cut-out of a bundled pack shot, else the pack
 * photo itself (e.g. a new upload with no transparent version yet).
 */
export const packCutout = (pack: MediaShot, small = false) =>
  pack.cutout || (/^\/images\/products\/[^/]+\.jpg$/.test(pack.src) ? cutout(pack.src, small) : pack.src);

const FINE_SQUARE_EMULSION: ProductMedia = {
  pack: shot('fine-square-emulsion-pack', 'Vertex Fine Square emulsion, 20 litre pack'),
  gallery: [
    shot('fine-square-emulsion-house', 'A modern house with a deep blue feature façade painted in Fine Square'),
    shot('fine-square-emulsion-rain', 'Rain beading on a blue exterior wall finished in Fine Square'),
    shot('fine-square-emulsion-painter', 'A Vertex applicator rolling Fine Square onto an exterior wall'),
    shot('fine-square-emulsion-living', 'A warm living room finished in Fine Square emulsion'),
  ],
};

const PU_ENAMEL_PACK = shot('pu-enamel-pack', 'Vertex PU Enamel high-performance finish, 1 litre tin');

export const PRODUCT_MEDIA: Record<string, ProductMedia> = {
  /* ---------------- Interior ---------------- */
  '/InteriorWallPaint/EasyWashLuxuryEmulsion': {
    pack: shot('easy-wash-pack', 'Vertex Easy Wash Stain Guard premium interior emulsion, 10 litre pack'),
    gallery: [
      shot('easy-wash-living', 'A calm living room finished in Easy Wash luxury emulsion'),
      shot('easy-wash-painter', 'A painter rolling Easy Wash onto a living-room wall'),
      shot('easy-wash-wipe', 'A mark being wiped off an Easy Wash wall with a cloth'),
      shot('easy-wash-cloth', 'Easy Wash wall being cleaned with a soft cloth'),
    ],
  },
  '/InteriorWallPaint/FineSquareInterriorEmulsion': FINE_SQUARE_EMULSION,

  /* ---------------- Exterior ---------------- */
  '/ExteriorWallPaint/TitaniumOne': {
    pack: shot('titanium-one-pack', 'Vertex Titanium ONE exterior rain block emulsion'),
    gallery: [
      shot('titanium-one-dusk', 'A contemporary home at dusk with Titanium ONE on the façade'),
      shot('titanium-one-rain-wall', 'Rain beading off a navy wall protected by Titanium ONE'),
      shot('titanium-one-driveway', 'Titanium ONE beside a freshly painted exterior after rain'),
      shot('titanium-one-painter', 'An applicator rolling Titanium ONE onto an exterior column'),
    ],
  },
  '/ExteriorWallPaint/PremiumEmlusion': {
    pack: shot('premium-emulsion-pack', 'Vertex Premium Emulsion, 20 litre pack'),
    gallery: [
      shot('premium-emulsion-living', 'A navy and wood living room painted in Premium Emulsion'),
      shot('premium-emulsion-wall', 'A deep blue wall finished in Premium Emulsion'),
      shot('premium-emulsion-painter', 'A painter applying Premium Emulsion to a living-room wall'),
      shot('premium-emulsion-studio', 'Premium Emulsion with shade cards and colour boards'),
    ],
  },
  '/ExteriorWallPaint/ACMEExteriorEmulsion': {
    pack: shot('acme-pack', 'Vertex ACME exterior emulsion, 20 litre pack'),
    gallery: [
      shot('acme-villa', 'A two-storey villa finished with ACME exterior emulsion'),
      shot('acme-green-wall', 'A deep green exterior wall painted in ACME'),
      shot('acme-cottage', 'A green cottage exterior protected by ACME'),
      shot('acme-painter', 'A painter applying ACME to an exterior wall'),
    ],
  },
  '/ExteriorWallPaint/ACMEWRP': {
    pack: shot('acme-pack', 'Vertex ACME exterior emulsion, 20 litre pack'),
    gallery: [shot('acme-villa', 'A villa exterior finished in the Vertex ACME range'), shot('acme-green-wall', 'A green exterior wall in the ACME range')],
  },
  '/ExteriorWallPaint/SilkShine_SuperGloss': {
    pack: shot('silk-shine-super-gloss-pack', 'Vertex Silk Shine Super Gloss, 20 litre pack'),
    gallery: [
      shot('silk-shine-super-gloss-lounge', 'A lounge with a high-gloss magenta wall in Silk Shine Super Gloss'),
      shot('silk-shine-super-gloss-wall', 'A mirror-like magenta wall finished in Silk Shine Super Gloss'),
      shot('silk-shine-super-gloss-painter', 'A painter rolling Silk Shine Super Gloss in a rose shade'),
      shot('silk-shine-super-gloss-roller', 'Silk Shine Super Gloss with a roller and tray'),
    ],
  },
  '/ExteriorWallPaint/UltraWRPExteriorEmulsion': {
    pack: shot('ultra-wrp-pack', 'Vertex Ultra WRP exterior wall sealer, 20 litre pack'),
    gallery: [
      shot('ultra-wrp-site', 'Ultra WRP on site beside a freshly sealed exterior'),
      shot('ultra-wrp-beading', 'Water beading on an exterior wall sealed with Ultra WRP'),
      shot('ultra-wrp-wall', 'A textured exterior wall sealed with Ultra WRP'),
      shot('ultra-wrp-applicator', 'An applicator rolling Ultra WRP onto an exterior wall'),
    ],
  },
  '/Exterior/UltraWrp': {
    pack: shot('ultra-wrp-pack', 'Vertex Ultra WRP exterior wall sealer, 20 litre pack'),
    gallery: [shot('ultra-wrp-beading', 'Water beading on a wall sealed with Ultra WRP'), shot('ultra-wrp-site', 'Ultra WRP on site')],
  },
  '/ExteriorWallPaint/FineSquareExteriorEmulsion': FINE_SQUARE_EMULSION,
  '/ExteriorWallPaint/FineSquareHiSheen': FINE_SQUARE_EMULSION,
  '/Exterior/FineSquare': FINE_SQUARE_EMULSION,

  /* ---------------- Undercoat ---------------- */
  '/UnderCoat/PremiumWallPutty': {
    pack: shot('premium-wall-putty-pack', 'Vertex Premium Wall Putty, 40 kg bag'),
    gallery: [
      shot('premium-wall-putty-applicator', 'An applicator spreading Premium Wall Putty on a wall'),
      shot('premium-wall-putty-trowel', 'A smooth coat of Premium Wall Putty being trowelled on'),
      shot('premium-wall-putty-room', 'Premium Wall Putty ready for use in a bright room'),
      shot('premium-wall-putty-site', 'Premium Wall Putty on a building site'),
    ],
  },
  '/UnderCoat/TitaniumWallPutty': {
    pack: shot('titanium-wall-putty-pack', 'Titanium ONE super-strong waterproof wall putty'),
    gallery: [
      shot('titanium-wall-putty-applicator', 'An applicator levelling a wall with Titanium ONE putty'),
      shot('titanium-wall-putty-stone', 'Titanium ONE wall putty on a stone plinth'),
      shot('titanium-wall-putty-trowel', 'Titanium ONE wall putty with a trowel'),
    ],
  },
  '/UnderCoat/ultraWallSealer3IN1': {
    pack: shot('ultra-3in1-sealer-pack', 'Vertex Ultra 3-in-1 wall sealer, 20 litre pack'),
    gallery: [],
  },
  '/UnderCoat/ZinPhosphatePrimerWhite': {
    pack: shot('zinc-phosphate-primer-pack', 'Vertex Metal Guard zinc phosphate anti-corrosive primer, 1 litre'),
    gallery: [],
  },

  /* ---------------- Metal & wood ---------------- */
  '/MetalPaints/1K_PU_HighPerformanceEnamel': {
    pack: PU_ENAMEL_PACK,
    gallery: [
      shot('pu-enamel-railing', 'A metal railing being finished in Vertex PU Enamel'),
      shot('pu-enamel-door', 'Metal railings and a timber door finished in PU Enamel'),
      shot('sample-boards-c', 'Vertex dealer sample boards including 1K PU Enamel metal finishes'),
    ],
  },
  '/WoodPaints/1K_PU_HighPerformanceEnamel': {
    pack: PU_ENAMEL_PACK,
    gallery: [
      shot('pu-enamel-wood-brush', 'A timber panel being brushed with Vertex PU Enamel'),
      shot('pu-enamel-wood-beading', 'Water beading on a wood surface finished in PU Enamel'),
      shot('sample-boards-c', 'Vertex dealer sample boards including 1K PU Enamel wood finishes'),
    ],
  },
  '/MetalPaints/PU_AluminumPaint': {
    pack: shot('pu-aluminium-pack', 'Vertex PU Aluminium high-performance paint, 1 litre tin'),
    gallery: [],
  },

  /* ---------------- Waterproofing ---------------- */
  '/WaterProofing/RoofDampGuard': {
    pack: shot('cool-damp-proof-pack', 'Vertex Cool Damp Proof roof coating, 20 litre pack'),
    gallery: [
      shot('cool-damp-proof-terrace', 'A terrace coated in white Cool Damp Proof'),
      shot('cool-damp-proof-roof-applicator', 'An applicator coating a roof with Cool Damp Proof'),
      shot('cool-damp-proof-roller', 'Cool Damp Proof with a roller on a finished roof'),
      shot('cool-damp-proof-drain', 'Cool Damp Proof beside a roof drain'),
    ],
  },
  '/WaterProofing/VertexCoolDampProof': {
    pack: shot('damp-proof-putty-pack', 'Vertex silicon-based damp proof putty, 20 kg bag'),
    gallery: [
      shot('damp-proof-putty-applicator', 'An applicator spreading silicon-based damp proof putty'),
      shot('damp-proof-putty-repair', 'A damp-damaged wall being repaired with damp proof putty'),
      shot('damp-proof-putty-beading', 'Water beading on a wall treated with damp proof putty'),
      shot('damp-proof-putty-hall', 'Damp proof putty in a finished hallway'),
    ],
  },
  '/WaterProofing/WaterproofingTexture': {
    pack: shot('fine-square-texture-pack', 'Vertex Fine Square premium waterproof texture coating'),
    gallery: [
      shot('fine-square-texture-villa', 'A villa entrance finished in Fine Square waterproof texture'),
      shot('fine-square-texture-trowel', 'An applicator trowelling Fine Square texture onto a wall'),
      shot('fine-square-texture-samples', 'Fine Square texture samples being applied'),
      shot('fine-square-texture-wall', 'A textured wall finished in Fine Square'),
    ],
  },
};

export const getProductMedia = (route: string): ProductMedia | undefined => PRODUCT_MEDIA[route];

/** Category-level range photography. */
export const RANGE_MEDIA = {
  metalGuard: shot('metal-guard-pack', 'Vertex Metal Guard metal paint, 1 litre tin'),
  sampleBoards: [
    shot('sample-boards-a', 'Vertex product sample boards: rustic textures, wall putty and 1K PU enamel'),
    shot('sample-boards-b', 'Vertex dealer sample boards showing textures, putty stages and enamel finishes'),
    shot('sample-boards-c', 'Close-up of Vertex sample boards with texture codes and enamel shades'),
  ],
};

/** Past dealer & contractor events (personal contact details removed from the artwork). */
export const EVENTS = [
  {
    title: 'Exclusive paint contractors meet',
    date: '25 July 2026',
    place: 'Balaji Hotel, Raikot (Ludhiana)',
    host: 'Janta Iron Store, Raikot',
    image: '/images/events/contractor-meet-25-jul.jpg',
    alt: 'Invitation to the Vertex paint contractors meet at Balaji Hotel, Raikot on 25 July 2026',
  },
  {
    title: 'Exclusive paint contractors meet',
    date: '29 July 2026',
    place: 'Balaji Hotel, Raikot (Ludhiana)',
    host: 'Janta Iron Store, Raikot',
    image: '/images/events/contractor-meet-29-jul.jpg',
    alt: 'Invitation to the Vertex paint contractors meet at Balaji Hotel, Raikot on 29 July 2026',
  },
  {
    title: 'Contractors meet — product showcase',
    date: '25 July 2026',
    place: 'Balaji Hotel, Raikot (Ludhiana)',
    host: 'Janta Iron Store, Raikot',
    image: '/images/events/contractor-meet-25-jul-b.jpg',
    alt: 'Vertex contractors meet invitation showing Titanium ONE, Easy Wash, Cool Damp Proof and the Colour Mode tinting system',
  },
];
